import {
  SearchEngine,
  SearchEngineConfiguration,
  buildContext,
  buildDictionaryFieldContext,
  buildSearchEngine,
  getOrganizationEndpoints,
} from '@coveo/atomic-react';
import { createAnalyticsClientMiddleware } from '@lib/coveo/analyticsClientMiddleware';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { interceptSearchMiddleware } from '@lib/coveo/interceptSearchMiddleware';
import {
  getNewSearchToken,
  getSavedSearchToken,
  getSearchTokenGroups,
} from '@lib/coveo/searchTokenHelpers';
import * as Sentry from '@sentry/nextjs';
import { clearStableFacetValues } from './hooks/useStableFacetValues';

type InitStep = 'token_read' | 'token_fetch' | 'build_engine' | 'context_setup';
type TokenSource = 'cache' | 'fresh' | 'none';

export type InitEngineOptions = {
  locale: string;
  currency: string;
  analyticsEnabled: boolean;
};

export function createEngineModule(hub: CoveoSearchHub): {
  initEngine: (options: InitEngineOptions) => Promise<SearchEngine>;
  engine: () => SearchEngine;
  resetEngine: () => void;
} {
  let singleton: SearchEngine | null = null;
  let initPromise: Promise<SearchEngine> | null = null;
  const analyticsClientMiddleware = createAnalyticsClientMiddleware(
    () => singleton?.state.pagination?.numberOfResults
  );

  function getConfiguration(
    searchToken: string,
    analyticsEnabled: boolean,
    locale: string
  ): SearchEngineConfiguration {
    const organizationId = process.env.NEXT_PUBLIC_COVEO_ORGANIZATION_ID!;
    return {
      accessToken: searchToken,
      organizationId,
      organizationEndpoints: getOrganizationEndpoints(organizationId),
      renewAccessToken: () =>
        getNewSearchToken(hub, { step: 'token_renew' }).then((t) => {
          if (t == null) {
            // Coveo SDK requires a string; the helper has already captured the
            // failure to Sentry. Drop a breadcrumb so any 401 storm that
            // follows is correlatable with this renew miss.
            Sentry.addBreadcrumb({
              category: 'coveo.renew',
              message: 'renew_returned_empty',
              level: 'warning',
              data: { hub },
            });
            return '';
          }
          return t;
        }),
      name: hub,
      search: { searchHub: hub, locale },
      analytics: { enabled: analyticsEnabled, analyticsClientMiddleware },
    };
  }

  function updateContext(eng: SearchEngine, options: InitEngineOptions): void {
    try {
      buildDictionaryFieldContext(eng).set({
        ec_price: options.currency,
        ec_price_filter: options.currency,
      });
      buildContext(eng).add('language', options.locale);
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTags({
          'coveo.hub': hub,
          'coveo.step': 'context_update',
        });
        scope.setContext('coveo_init', {
          hub,
          locale: options.locale,
          currency: options.currency,
          analyticsEnabled: options.analyticsEnabled,
        });
        scope.setFingerprint(['coveo-init', hub, 'context_update']);
        Sentry.captureException(error);
      });
      throw error;
    }
  }

  async function doInit(options: InitEngineOptions): Promise<SearchEngine> {
    const { locale, currency, analyticsEnabled } = options;

    let step: InitStep = 'token_read';
    let tokenSource: TokenSource = 'none';
    let hadCachedToken = false;
    // null = decode never ran or threw; a number = successfully decoded.
    let userGroupsCount: number | null = null;

    try {
      Sentry.addBreadcrumb({
        category: 'coveo.init',
        message: 'token_read',
        level: 'info',
        data: { hub },
      });

      const savedToken = getSavedSearchToken(hub);
      let searchToken: string | null = null;

      if (savedToken && savedToken !== '{}') {
        hadCachedToken = true;
        try {
          const parsed = JSON.parse(savedToken);
          if (typeof parsed === 'string' && parsed.trim()) {
            searchToken = parsed;
            tokenSource = 'cache';
          }
        } catch {
          // fall through to fetch a new token; hadCachedToken stays true so
          // Sentry can distinguish "corrupted cache" from "no cache".
        }
      }

      if (!searchToken) {
        step = 'token_fetch';
        Sentry.addBreadcrumb({
          category: 'coveo.init',
          message: 'token_fetch',
          level: 'info',
          data: { hub },
        });
        searchToken = await getNewSearchToken(hub, { rethrowOnError: true });
        if (searchToken) tokenSource = 'fresh';
      }

      if (!searchToken) {
        throw new Error(
          `Failed to initialize Coveo engine (${hub}): missing search token.`
        );
      }

      step = 'build_engine';
      Sentry.addBreadcrumb({
        category: 'coveo.init',
        message: 'build_engine',
        level: 'info',
        data: { hub },
      });
      singleton = buildSearchEngine({
        configuration: getConfiguration(searchToken, analyticsEnabled, locale),
        middlewares: [interceptSearchMiddleware],
      });

      step = 'context_setup';
      Sentry.addBreadcrumb({
        category: 'coveo.init',
        message: 'context_setup',
        level: 'info',
        data: { hub },
      });
      buildDictionaryFieldContext(singleton).set({
        ec_price: currency,
        ec_price_filter: currency,
      });
      const context = buildContext(singleton);
      context.add('website', 'assetstore');
      context.add('language', locale);
      const userGroups = getSearchTokenGroups(searchToken) || [];
      userGroupsCount = userGroups.length;
      context.add('userGroups', userGroups);

      Sentry.addBreadcrumb({
        category: 'coveo.init',
        message: 'engine_ready',
        level: 'info',
        data: { hub },
      });

      return singleton;
    } catch (error) {
      // Unconditionally clear the module-level singleton. For early steps it's already null;
      // the load-bearing case is a post-build_engine failure (e.g. context setup throwing on
      // a malformed cached token) where we'd otherwise cache a half-built engine and serve it
      // from the `if (singleton)` short-circuit on the next initEngine() call.
      singleton = null;
      Sentry.withScope((scope) => {
        scope.setTags({
          'coveo.hub': hub,
          'coveo.step': step,
          'coveo.had_cached_token': String(hadCachedToken),
          'coveo.token_source': tokenSource,
        });
        scope.setContext('coveo_init', {
          hub,
          locale,
          currency,
          analyticsEnabled,
          hadCachedToken,
          tokenSource,
          userGroupsCount,
        });
        scope.setFingerprint(['coveo-init', hub, step]);
        Sentry.captureException(error);
      });
      throw error;
    }
  }

  function initEngine(options: InitEngineOptions): Promise<SearchEngine> {
    if (singleton) {
      try {
        updateContext(singleton, options);
      } catch (error) {
        return Promise.reject(error);
      }
      return Promise.resolve(singleton);
    }

    if (initPromise) return initPromise;

    initPromise = doInit(options).catch((error) => {
      initPromise = null;
      throw error;
    });

    return initPromise;
  }

  function engine(): SearchEngine {
    if (!singleton) {
      throw new Error(
        `Coveo engine (${hub}) not initialized. Call initEngine() before using engine().`
      );
    }
    return singleton;
  }

  function resetEngine(): void {
    if (singleton) clearStableFacetValues(singleton);
    singleton = null;
    initPromise = null;
  }

  return { initEngine, engine, resetEngine };
}
